import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { getColors, fonts } from '../theme';
import {
  Calendar,
  Target,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ArrowLeft,
  Infinity as InfinityIcon,
  Shield,
} from 'lucide-react';
import CheckoutModal from '../components/CheckoutModal';

const UpgradePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, user, habits, tasks, goals, events } = useApp();
  const {
    tier,
    isPro,
    loading: subscriptionLoading,
    tierConfig,
  } = useSubscription();
  const colors = getColors(isDark);
  
  const featuresRef = useRef(null);
  const faqRef = useRef(null);
  
  // Billing toggle
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  
  // FAQ accordion
  const [openFaq, setOpenFaq] = useState(null);
  
  // Checkout modal
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [checkoutPriceType, setCheckoutPriceType] = useState('yearly');
  
  // Check for context (e.g., which limit they hit)
  const limitContext = searchParams.get('limit');
  
  // Pricing
  const monthlyPrice = 5;
  const yearlyPrice = 50;
  const yearlyMonthly = (yearlyPrice / 12).toFixed(2);
  const savings = (monthlyPrice * 12) - yearlyPrice;
  
  // Current usage for context
  const currentUsage = {
    habits: { current: habits?.length || 0, limit: tierConfig?.limits?.maxHabits || 5 },
    tasks: { current: tasks?.length || 0, limit: tierConfig?.limits?.maxTasks || 5 },
    goals: { current: goals?.length || 0, limit: tierConfig?.limits?.maxGoals || 3 },
    events: { current: events?.length || 0, limit: tierConfig?.limits?.maxEvents || 10 },
  };
  
  // Handle checkout
  const handleUpgrade = (period) => {
    setCheckoutPriceType(period);
    setShowCheckoutModal(true);
  };
  
  // If already Pro, redirect to settings
  useEffect(() => {
    if (!subscriptionLoading && isPro) {
      navigate('/settings');
    }
  }, [isPro, subscriptionLoading, navigate]);
  
  // Pro benefits for feature cards
  const proFeatures = [
    {
      icon: InfinityIcon,
      title: 'Unlimited Everything',
      description: 'Create as many habits, tasks, goals, and events as you need',
    },
    {
      icon: Calendar,
      title: 'Google Calendar Sync',
      description: 'Two-way sync with your Google Calendar for seamless scheduling',
    },
    {
      icon: TrendingUp,
      title: 'Advanced Analytics',
      description: 'Deep insights into your productivity patterns and progress',
    },
    {
      icon: Target,
      title: 'Goal Milestones',
      description: 'Break down goals into achievable milestones with progress tracking',
    },
  ];
  
  // Comparison features
  const comparisonFeatures = [
    { name: 'Habits', free: '5', pro: '∞', highlight: limitContext === 'habits' },
    { name: 'Tasks', free: '5', pro: '∞', highlight: limitContext === 'tasks' },
    { name: 'Goals', free: '3', pro: '∞', highlight: limitContext === 'goals' },
    { name: 'Events', free: '10', pro: '∞', highlight: limitContext === 'events' },
    { name: 'Google Calendar', free: false, pro: true },
    { name: 'Habit Templates', free: false, pro: true },
    { name: 'Goal Milestones', free: false, pro: true },
    { name: 'Priority Support', free: false, pro: true },
    { name: 'Custom Themes', free: true, pro: true },
    { name: 'Streak Tracking', free: true, pro: true },
  ];
  
  // FAQ items
  const faqItems = [
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes! You can cancel your Pro subscription at any time from your Settings page. You\'ll continue to have Pro access until the end of your billing period.'
    },
    {
      question: 'What happens to my data if I downgrade?',
      answer: 'Your data is never deleted. If you downgrade to Free, you\'ll still have access to all your existing habits, tasks, goals, and events.'
    },
    {
      question: 'Do you offer refunds?',
      answer: 'Yes, we offer a 30-day money-back guarantee. If you\'re not satisfied with Pro, contact us within 30 days for a full refund.'
    },
    {
      question: 'How does annual billing work?',
      answer: `Annual billing is charged once per year at $${yearlyPrice}. This saves you $${savings} compared to monthly billing.`
    },
  ];

  if (subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.bg }}>
        <div 
          className="w-8 h-8 border-2 rounded-full animate-spin"
          style={{ borderColor: colors.border, borderTopColor: colors.accent }}
        />
      </div>
    );
  }

  const ink = colors.textPrimary;
  const rule = `1px solid ${colors.border}`;
  const inkRule = `1px solid ${ink}`;
  const Cell = ({ value }) => {
    if (value === true || value === false) {
      return (
        <span
          aria-label={value ? 'Included' : 'Not included'}
          className="inline-block w-2.5 h-2.5"
          style={{ border: `1px solid ${value ? ink : colors.borderStrong}`, backgroundColor: value ? ink : 'transparent' }}
        />
      );
    }
    return <span className="font-mono text-sm">{value === '∞' ? 'Unlimited' : value}</span>;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bg, color: ink }}>
      <div className="max-w-[880px] mx-auto px-5 pb-24">
        <header className="flex items-end justify-between gap-4" style={{ borderBottom: inkRule }}>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm pt-4 pb-3.5 hover:underline underline-offset-4"
            style={{ color: colors.textSecondary }}
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            Back
          </button>
          <span className="text-[22px] leading-none pb-3.5" style={{ fontFamily: fonts.serif }}>mind.</span>
        </header>

        <div className="pt-10 pb-7 sm:pt-12">
          <p className="font-mono text-xs mb-2.5" style={{ color: colors.textSecondary }}>Plans · prices in USD</p>
          <h1 className="text-[44px] sm:text-[64px] font-semibold leading-[.95]" style={{ letterSpacing: '-0.045em' }}>
            Upgrade to Pro
          </h1>
          <p className="text-lg leading-relaxed mt-5 max-w-[58ch]" style={{ color: colors.textSecondary }}>
            Everything unlimited, Google Calendar sync, goal milestones, and your productivity score.
          </p>
          {limitContext && currentUsage[limitContext] && (
            <p className="font-mono text-xs mt-5 py-2.5 px-3 inline-block" style={{ border: inkRule }}>
              You've used {currentUsage[limitContext].current} of {currentUsage[limitContext].limit} {limitContext} on the free plan.
            </p>
          )}
        </div>

        <div className="inline-flex font-mono text-xs" style={{ border: `1px solid ${colors.borderStrong}` }} role="group" aria-label="Billing period">
          {['monthly', 'yearly'].map(period => (
            <button
              key={period}
              onClick={() => setBillingPeriod(period)}
              aria-pressed={billingPeriod === period}
              className="px-3 py-1.5"
              style={{
                backgroundColor: billingPeriod === period ? colors.accent : 'transparent',
                color: billingPeriod === period ? colors.onAccent : colors.textSecondary,
              }}
            >
              {period === 'yearly' ? `Yearly · save ${Math.round((savings / (monthlyPrice * 12)) * 100)}%` : 'Monthly'}
            </button>
          ))}
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-9 md:gap-8 mt-6">
          <div className="flex flex-col pt-4" style={{ borderTop: inkRule }}>
            <h2 className="text-xl font-semibold tracking-tight">Free</h2>
            <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>Get started with habit tracking</p>
            <p className="flex items-baseline gap-2 mt-5 font-mono">
              <span className="text-5xl font-light" style={{ letterSpacing: '-0.04em' }}>$0</span>
              <span className="text-[13px]" style={{ color: colors.textSecondary }}>forever</span>
            </p>
            <p className="font-mono text-xs mt-1" style={{ color: colors.textSecondary }}>&nbsp;</p>
            <div className="mt-6">
              <button disabled className="px-4 py-2.5 text-sm opacity-60 cursor-default" style={{ border: `1px solid ${colors.borderStrong}` }}>
                {tier === 'free' ? 'Your current plan' : 'Included'}
              </button>
            </div>
          </div>
          <div className="flex flex-col pt-4" style={{ borderTop: inkRule }}>
            <h2 className="text-xl font-semibold tracking-tight">Pro</h2>
            <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>Unlock all features</p>
            <p className="flex items-baseline gap-2 mt-5 font-mono">
              <span className="text-5xl font-light" style={{ letterSpacing: '-0.04em' }}>${billingPeriod === 'yearly' ? yearlyPrice : monthlyPrice}</span>
              <span className="text-[13px]" style={{ color: colors.textSecondary }}>{billingPeriod === 'yearly' ? 'per year' : 'per month'}</span>
            </p>
            <p className="font-mono text-xs mt-1" style={{ color: colors.textSecondary }}>
              {billingPeriod === 'yearly' ? `$${yearlyMonthly} a month · save $${savings} a year` : `or $${yearlyPrice} a year`}
            </p>
            <div className="mt-6">
              <button
                onClick={() => handleUpgrade(billingPeriod)}
                className="px-4 py-2.5 text-sm transition-opacity hover:opacity-85 inline-flex items-center gap-2"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                Upgrade to Pro
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Comparison */}
        <section className="mt-14">
          <div className="grid grid-cols-[minmax(0,1fr)_80px_80px] sm:grid-cols-[minmax(0,1fr)_120px_120px] gap-x-3 items-baseline pb-2" style={{ borderBottom: inkRule }}>
            <h2 className="text-[15px] font-semibold tracking-tight">Compare</h2>
            <span className="font-mono text-xs" style={{ color: colors.textSecondary }}>Free</span>
            <span className="font-mono text-xs" style={{ color: colors.textSecondary }}>Pro</span>
          </div>
          {comparisonFeatures.map(f => (
            <div
              key={f.name}
              className="grid grid-cols-[minmax(0,1fr)_80px_80px] sm:grid-cols-[minmax(0,1fr)_120px_120px] gap-x-3 items-center min-h-[48px]"
              style={{ borderBottom: rule, backgroundColor: f.highlight ? colors.bgTertiary : 'transparent' }}
            >
              <span className="py-3 text-[15px]">
                {f.name === 'Custom Themes' ? 'Paper and chalkboard themes' : f.name}
                {f.highlight && <span className="font-mono text-[11px] ml-2 underline underline-offset-[3px]">your limit</span>}
              </span>
              <span style={{ color: colors.textSecondary }}><Cell value={f.free} /></span>
              <span><Cell value={f.pro} /></span>
            </div>
          ))}
        </section>

        {/* What Pro adds */}
        <section ref={featuresRef} className="mt-14">
          <div className="pb-2" style={{ borderBottom: inkRule }}>
            <h2 className="text-[15px] font-semibold tracking-tight">What Pro adds</h2>
          </div>
          {proFeatures.map(feature => (
            <div key={feature.title} className="flex gap-4 items-start py-4" style={{ borderBottom: rule }}>
              <feature.icon size={18} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" style={{ color: colors.textSecondary }} />
              <div>
                <p className="font-semibold text-[15px]">{feature.title}</p>
                <p className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>{feature.description}</p>
              </div>
            </div>
          ))}
        </section>

        {/* FAQ */}
        <section ref={faqRef} className="mt-14">
          <div className="pb-2" style={{ borderBottom: inkRule }}>
            <h2 className="text-[15px] font-semibold tracking-tight">Questions</h2>
          </div>
          {faqItems.map((item, index) => {
            const open = openFaq === index;
            return (
              <div key={item.question} style={{ borderBottom: rule }}>
                <button
                  onClick={() => setOpenFaq(open ? null : index)}
                  className="w-full flex justify-between items-center gap-4 min-h-[52px] py-3 text-left text-[15px]"
                  aria-expanded={open}
                >
                  {item.question}
                  {open ? <ChevronUp size={16} strokeWidth={1.5} /> : <ChevronDown size={16} strokeWidth={1.5} />}
                </button>
                {open && (
                  <p className="pb-4 text-sm leading-relaxed max-w-[62ch]" style={{ color: colors.textSecondary }}>{item.answer}</p>
                )}
              </div>
            );
          })}
        </section>

        <div className="mt-14 pt-5 flex flex-wrap items-center gap-x-6 gap-y-3" style={{ borderTop: inkRule }}>
          <button
            onClick={() => handleUpgrade(billingPeriod)}
            className="px-5 py-3 text-[15px] transition-opacity hover:opacity-85"
            style={{ backgroundColor: colors.accent, color: colors.onAccent }}
          >
            Get Pro · ${billingPeriod === 'yearly' ? `${yearlyPrice}/year` : `${monthlyPrice}/month`}
          </button>
          <span className="font-mono text-xs flex items-center gap-2" style={{ color: colors.textSecondary }}>
            <Shield size={13} strokeWidth={1.5} /> Secure payment by Stripe · cancel anytime in Settings
          </span>
        </div>
      </div>

      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        priceType={checkoutPriceType}
        userId={user?.userId}
        userEmail={user?.email}
      />
    </div>
  );
};

export default UpgradePage;
